import { useContext, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { UserContext } from '../context/UserContext';
import './home.css';

export default function LurkerRadar() {
  const { user } = useContext(UserContext);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const isAdmin = Array.isArray(user?.roles) && (user.roles.includes('admin') || user.roles.includes('owner'));
  const [pointsPerStartedHour, setPointsPerStartedHour] = useState('');
  const [pricingError, setPricingError] = useState('');
  const [pricingOk, setPricingOk] = useState('');
  const [pricingBusy, setPricingBusy] = useState(false);

  const load = async () => {
    setError('');
    const res = await api.get('/lurker2/radar');
    setData(res.data);
  };

  useEffect(() => {
    load().catch((e) => setError(e?.response?.data?.error || e?.message || 'Konnte Radar nicht laden'));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        setPricingError('');
        const res = await api.get('/admin/supportplan-cost');
        const v = res.data?.pointsPerStartedHour;
        setPointsPerStartedHour(String(typeof v === 'number' ? v : 30));
      } catch (e) {
        setPricingError(e?.response?.data?.error || e?.message || 'Konnte Supportplan-Kosten nicht laden');
      }
    })();
  }, [isAdmin]);

  const savePricing = async () => {
    try {
      setPricingOk('');
      setPricingError('');
      setPricingBusy(true);
      const n = Math.max(0, Math.floor(Number(pointsPerStartedHour)));
      if (!Number.isFinite(n)) {
        setPricingError('Ungültiger Wert');
        return;
      }
      const res = await api.put('/admin/supportplan-cost', { pointsPerStartedHour: n });
      setPointsPerStartedHour(String(res.data?.pointsPerStartedHour ?? n));
      setPricingOk('Gespeichert');
    } catch (e) {
      setPricingError(e?.response?.data?.error || e?.message || 'Konnte nicht speichern');
    } finally {
      setPricingBusy(false);
    }
  };

  const live = useMemo(() => (data?.live || []), [data]);

  return (
    <div className="home-page">
      <section style={{ width: '100vw', margin: 0, padding: 0, position: 'relative', left: '-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Lurker 2.0 · Live‑Radar</h2>
        <p className="hero-desc" style={{ margin: '0 auto 24px', textAlign: 'center', maxWidth: 860 }}>
          Geplante Slots vs. tatsächlich live (Twitch Helix). Sortiert nach Supportbedarf.
        </p>
      </section>

      <section style={{ width: '100%', maxWidth: 1200, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        {error && <div style={{ color: 'tomato', marginBottom: 10 }}>{error}</div>}

        {isAdmin && (
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12, marginBottom: 12 }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Supportplan-Kosten (Admin)</div>
            <div style={{ fontSize: 12, color: '#bbb', marginBottom: 8 }}>
              Punkte pro angefangene Stunde für Supportplan-Einträge. <b>0 = deaktiviert</b>.
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="number"
                min={0}
                step={1}
                value={pointsPerStartedHour}
                onChange={(e) => setPointsPerStartedHour(e.target.value)}
                style={{ width: 160 }}
              />
              <button onClick={() => savePricing().catch(() => {})} disabled={pricingBusy}>
                {pricingBusy ? 'Speichern…' : 'Speichern'}
              </button>
              {pricingOk && <div style={{ fontSize: 12, color: '#9f9' }}>{pricingOk}</div>}
              {pricingError && <div style={{ fontSize: 12, color: 'tomato' }}>{pricingError}</div>}
            </div>
          </div>
        )}

        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12, marginBottom: 12 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>Wie wird „Need“ berechnet?</div>
          <div style={{ fontSize: 13, color: '#ddd', lineHeight: 1.5 }}>
            „Need (%)“ ist der <b>Anteil am gesamten Supportbedarf</b> über alle <b>aktuell live</b> Streams.
            Die Summe über alle Zeilen ist immer <b>100%</b> (Rundung kann minimal abweichen).
            Dafür wird serverseitig zuerst pro Stream ein Score berechnet:
            {' '}
            <code style={{ background: 'rgba(0,0,0,0.35)', padding: '2px 6px', borderRadius: 6 }}>
              needScore = 1.5*(1/(viewer+1)) + 0.8*freshBoost + (planned ? 2.0 : 0)
            </code>
            {' '}und anschließend auf alle Live‑Streams verteilt:
            {' '}
            <code style={{ background: 'rgba(0,0,0,0.35)', padding: '2px 6px', borderRadius: 6 }}>
              need% = needScore / sum(needScore aller Live‑Streams) * 100
            </code>
          </div>
          <div style={{ fontSize: 12, color: '#bbb', marginTop: 8, lineHeight: 1.5 }}>
            <div><b>viewer</b>: aktueller Helix‑Viewer‑Count (≥ 0). Je mehr Viewer, desto kleiner der Term <code style={{ background: 'rgba(0,0,0,0.35)', padding: '1px 4px', borderRadius: 6 }}>1/(viewer+1)</code>.</div>
            <div><b>freshBoost</b>: basiert auf Stream‑Alter in Minuten (serverseitig):</div>
            <div style={{ marginLeft: 12 }}>
              <code style={{ background: 'rgba(0,0,0,0.35)', padding: '1px 4px', borderRadius: 6 }}>freshBoost = clamp((15 - ageMin)/15, 0..1)</code>
              {' '}→ in den ersten 15 Minuten gibt’s bis zu +0.8, danach 0.
            </div>
            <div><b>planned</b>: <code style={{ background: 'rgba(0,0,0,0.35)', padding: '1px 4px', borderRadius: 6 }}>true</code>, wenn der Login aktuell in einem Supportplan‑Slot liegt → fixer Boost +2.0.</div>
            <div style={{ marginTop: 6 }}>
              Wenn z.B. 4 Streams exakt gleich bewertet werden, hat jeder 25%. Wenn ein Stream einen höheren Score hat,
              bekommt er entsprechend mehr Prozent vom 100%-Gesamtbudget.
            </div>
            <div style={{ marginTop: 6 }}>
              <b>Wichtig:</b> Wenn nur <b>ein</b> Stream live ist, erhält dieser immer <b>100%</b> (weil 100% auf die aktuell live Streams verteilt werden).
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <button onClick={() => load().catch(() => {})}>Aktualisieren</button>
          {data?.now && <div style={{ alignSelf: 'center', fontSize: 12, color: '#bbb' }}>Stand: {new Date(data.now).toLocaleString()}</div>}
        </div>

        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12 }}>
          {!data && <div>Lade…</div>}
          {data && (
            <>
              <div style={{ fontSize: 12, color: '#bbb', marginBottom: 8 }}>
                Live: {data.liveCount} · Geplant jetzt: {data.plannedNowCount}
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                      <th style={{ padding: '8px 6px' }}>Streamer</th>
                      <th style={{ padding: '8px 6px' }}>Login</th>
                      <th style={{ padding: '8px 6px' }}>Viewer</th>
                      <th style={{ padding: '8px 6px' }}>Kategorie</th>
                      <th style={{ padding: '8px 6px' }}>Geplant</th>
                      <th style={{ padding: '8px 6px' }}>Need (%)</th>
                      <th style={{ padding: '8px 6px' }}>Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {live.map((x) => (
                      <tr key={x.twitchLogin} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        <td style={{ padding: '8px 6px', fontWeight: 600 }}>{x.displayName}</td>
                        <td style={{ padding: '8px 6px', color: '#bbb' }}>{x.twitchLogin}</td>
                        <td style={{ padding: '8px 6px' }}>{x.viewerCount}</td>
                        <td style={{ padding: '8px 6px', color: '#bbb' }}>{x.gameName || '—'}</td>
                        <td style={{ padding: '8px 6px' }}>{x.planned ? 'ja' : 'nein'}</td>
                        <td style={{ padding: '8px 6px' }}>
                          {`${(typeof x.needPercent === 'number' ? x.needPercent : 0).toFixed(1)}%`}
                        </td>
                        <td style={{ padding: '8px 6px' }}>
                          <a className="btn" href={`/lurker/${encodeURIComponent(x.username)}`} style={{ padding: '6px 10px' }}>Profil</a>
                        </td>
                      </tr>
                    ))}
                    {live.length === 0 && (
                      <tr><td colSpan={7} style={{ padding: 10, color: '#bbb' }}>Keine Live‑Streams gefunden.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {(data.plannedNotLive || []).length > 0 && (
                <div style={{ marginTop: 12, fontSize: 12, color: '#bbb' }}>
                  Geplant aber nicht live: {(data.plannedNotLive || []).map(p => `${p.displayName} (${p.twitchLogin})`).join(' · ')}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
